import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import { getCanonicalTitle } from "../../common/util/Incentives/IncentiveRegistry";
import { ROUTE_PATHS } from "../../common/enums/routing-enums";
import { AWARD_CODES } from "../../common/enums/award-codes";
import defaultContent from "../../content/csi";
import commonContent from "../../content/common";
import { SELECTOR_TYPE, updateSelectorType } from "../../reducers/period";
import { ERROR_TYPE, ErrorPage } from "../ErrorPage/ErrorPage";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import { BannerTile } from "../../common/components/BannerTile/BannerTile";
import { track } from "../../common/util/tealium";
import TEALIUM from "../../common/enums/tealium-enums";
import { VolumeRequirementTile } from "../../common/components/RequirementTiles/VolumeRequirementTile/VolumeRequirementTile";
import BonusCalculation from "./BonusCalculation/BonusCalculation";
import BonusTracker from "./BonusTracker/BonusTracker";
import TransactionHistory from "./TransactionHistory/TransactionHistory";
import { useCsiOverviewData } from "./DataLayer";
import { getFullMonth, getYear } from "../../common/util/period";
import { storedLocale } from "../Authentication/util";
import { formatCurrencyAmount } from "../../common/util/formatCurrency";
import type {
  CsiBannerTile,
  CsiPerformanceBonusData,
} from "../../services/CustomerSalesIncentive/CustomerSalesIncentive.types";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import "./CustomerSalesIncentive.scss";

const CSI = () => {
  const { t } = useTranslation(["csi", "common"]);
  const symbol = t("disclaimerSymbol", { ns: "common", defaultValue: commonContent.disclaimerSymbol });
  const dispatch = useAppDispatch();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { configuration, shellConfig } = useAppSelector((state) => state.boot);
  const learnMoreLink = configuration.dashboardLinks?.learnMore?.customerSalesIncentive ?? "";
  const locale = storedLocale();
  const hasCustomerOverview =
    shellConfig.menuConfig
      ?.flatMap((nav) => nav.items)
      .some((item) => {
        const code = item.code;
        return Array.isArray(code)
          ? code.includes(AWARD_CODES.CUSTOMER_OVERVIEW)
          : code === AWARD_CODES.CUSTOMER_OVERVIEW;
      }) ?? false;

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
  }, [dispatch]);

  const { csiOverviewData, isLoading, errorStatus } = useCsiOverviewData(selectedPeriod, false);

  // Tealium tracking — track once per loaded period
  const trackedPeriod = useRef("");
  useEffect(() => {
    if (
      !isLoading &&
      !errorStatus &&
      csiOverviewData?.BannerTile?.ProgramActive &&
      trackedPeriod.current !== selectedPeriod
    ) {
      trackedPeriod.current = selectedPeriod;
      const bt = csiOverviewData.BannerTile;
      const pageSection = bt.Graduated ? "ineligible" : "eligible";
      const pageDetail = bt.CSIBonus === 0 ? "not_started" : bt.CSIBonus > 0 ? "achieved" : "data unavailable";

      track(
        TEALIUM.U_TAG_METHOD.VIEW,
        TEALIUM.EVENT_NAME.CLICK_ACTION,
        "customer_sales_incentive",
        pageSection,
        `${pageDetail}:${selectedPeriod}`,
      );
    }
  }, [isLoading, errorStatus, csiOverviewData, selectedPeriod]);

  // --- State Machine: Error Branches ---

  const pageTitle = getCanonicalTitle({ id: "csi" }, t, { symbol });

  if (errorStatus || !csiOverviewData) {
    return <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} isLoading={isLoading} />;
  }

  const {
    BannerTile: bannerTileData,
    BonusSchedules,
    PerformanceBonus: perfBonus,
    TransactionHistory: txHistory,
  } = csiOverviewData;

  if (!isLoading && !bannerTileData.ProgramActive) {
    return (
      <ErrorPage
        errorType={ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD}
        isLoading={isLoading}
        pageTitle={pageTitle}
        containerClassName="CustomerSalesIncentive"
      />
    );
  }

  // --- Formatting helpers ---

  const csiBonus = formatCurrencyAmount(bannerTileData.CSIBonus, bannerTileData.CSIBonusCurrency);

  const bonusCapLabel =
    bannerTileData.CSIBonusCapValue !== null && bannerTileData.CSIBonusCapCurrency
      ? t("bannerTile.bonusCapped", defaultContent.bannerTile.bonusCapped, {
          cap: formatCurrencyAmount(bannerTileData.CSIBonusCapValue, bannerTileData.CSIBonusCapCurrency),
        })
      : undefined;

  const txEntries = txHistory?.Entries ?? [];

  // --- State Machine: compute banner content ---
  // null = fall through to standard earning view

  const deniedPeriodTitle = t("deniedHeld.denied", defaultContent.deniedHeld.denied, {
    currentMonth: getFullMonth(selectedPeriod, locale),
    currentYear: getYear(selectedPeriod, locale),
    symbol,
  });
  const contactSubtitle = t("deniedHeld.contactCustomerCare", defaultContent.deniedHeld.contactCustomerCare);

  let bannerContent: ReactNode = null;

  if (bannerTileData.Held) {
    bannerContent = (
      <BannerTile
        isLoading={isLoading}
        title={t("deniedHeld.held", defaultContent.deniedHeld.held)}
        subtitle={t("deniedHeld.heldSubtitle", defaultContent.deniedHeld.heldSubtitle)}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        learnMoreLink={learnMoreLink}
      />
    );
  } else if (bannerTileData.Denied) {
    bannerContent = (
      <BannerTile
        isLoading={isLoading}
        title={deniedPeriodTitle}
        subtitle={contactSubtitle}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        learnMoreLink={learnMoreLink}
      />
    );
  } else if (bannerTileData.Graduated) {
    bannerContent = (
      <GraduatedBanner
        bannerTile={bannerTileData}
        perfBonus={perfBonus}
        bonusCapLabel={bonusCapLabel}
        isLoading={isLoading}
        learnMoreLink={learnMoreLink}
        deniedPeriodTitle={deniedPeriodTitle}
        contactSubtitle={contactSubtitle}
      />
    );
  } else if (bannerTileData.EligibilityMet === false) {
    bannerContent = (
      <BannerTile
        isLoading={isLoading}
        title={deniedPeriodTitle}
        subtitle={contactSubtitle}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        incentiveCapLabel={bonusCapLabel}
        learnMoreLink={learnMoreLink}
      />
    );
  } else if (bannerTileData.IsManuallyDenied) {
    bannerContent = (
      <BannerTile
        isLoading={isLoading}
        title={t("bannerTile.statusManuallyDenied", defaultContent.bannerTile.statusManuallyDenied)}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        learnMoreLink={learnMoreLink}
      />
    );
  } else if (bannerTileData.ManualApproval) {
    bannerContent = (
      <BannerTile
        isLoading={isLoading}
        title={t("manualApproval.message", defaultContent.manualApproval.message, { symbol })}
        subtitle={contactSubtitle}
        shouldDisplayAmount={true}
        bonusAmount={csiBonus}
        bonusAmountLabel={t("bannerTile.bonusAmountLabel", defaultContent.bannerTile.bonusAmountLabel)}
        learnMoreLink={learnMoreLink}
      />
    );
  }

  // --- Render: banner-only states share the same shell; standard view adds full content ---

  const isBannerOnly = bannerContent !== null;

  return (
    <main className="CustomerSalesIncentive">
      <PageTitleSection title={pageTitle} learnMoreLink={isBannerOnly ? undefined : learnMoreLink} />

      <div className="CustomerSalesIncentive__banner">{bannerContent}</div>

      {!isBannerOnly && (
        <>
          {bannerTileData.HasPPVRequirement && !bannerTileData.PPVExemptionApplied && (
            <section
              className="CustomerSalesIncentive__requirements CustomerSalesIncentive__requirements--ppv"
              aria-busy={isLoading}
            >
              <h2 className="CustomerSalesIncentive__requirements-title">
                {t("requirementsTitle", defaultContent.requirementsTitle)}
              </h2>
              <VolumeRequirementTile
                title={t("bonusCalculation.ppvRequired", defaultContent.bonusCalculation.ppvRequired, {
                  target: bannerTileData.TargetPPV,
                })}
                isLoading={isLoading}
                currentAmount={bannerTileData.CurrentPPV}
                targetAmount={bannerTileData.TargetPPV}
                met={bannerTileData.PPVRequirementMet}
                circleCheckmarkId="ppv-requirement"
                volumeType="PV"
              />
            </section>
          )}

          <BonusCalculation bannerTile={bannerTileData} perfBonus={perfBonus} isLoading={isLoading} />

          <BonusTracker
            bonusSchedules={BonusSchedules}
            currentCSILevel={bannerTileData.CSILevel}
            perfBonusLevel={perfBonus.PerfBonusLevel}
            currentGPV={perfBonus.CurrentGPV}
            isLoading={isLoading}
          />

          <TransactionHistory
            entries={txEntries}
            isLoading={isLoading}
            customerOverviewPath={hasCustomerOverview ? ROUTE_PATHS.CUSTOMER_LIST : undefined}
          />
        </>
      )}

      <BonusInfoFooter />
    </main>
  );
};

// --- Graduated Banner Sub-Component ---

type GraduatedBannerProps = {
  bannerTile: CsiBannerTile;
  perfBonus: CsiPerformanceBonusData;
  bonusCapLabel: string | undefined;
  isLoading: boolean;
  learnMoreLink: string;
  deniedPeriodTitle: string;
  contactSubtitle: string;
};

const GraduatedBanner = ({
  bannerTile,
  perfBonus,
  bonusCapLabel,
  isLoading,
  learnMoreLink,
  deniedPeriodTitle,
  contactSubtitle,
}: GraduatedBannerProps) => {
  const { t } = useTranslation(["csi", "common"]);
  const symbol = t("disclaimerSymbol", { ns: "common", defaultValue: commonContent.disclaimerSymbol });

  if (bannerTile.EligibilityMet === false) {
    // Graduated + met=false → "Not Eligible" + cap
    return (
      <BannerTile
        isLoading={isLoading}
        title={deniedPeriodTitle}
        subtitle={contactSubtitle}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        incentiveCapLabel={bonusCapLabel}
        learnMoreLink={learnMoreLink}
      />
    );
  }

  // Graduated + met=true or met=null → "Earning X% Performance Bonus"
  return (
    <>
      <BannerTile
        isLoading={isLoading}
        title={t("graduated.earningPerfBonus", defaultContent.graduated.earningPerfBonus, {
          level: perfBonus.PerfBonusLevel,
        })}
        subtitle={t("graduated.aboveRangeSubtitle", defaultContent.graduated.aboveRangeSubtitle, { symbol })}
        shouldDisplayAmount={false}
        bonusAmount=""
        bonusAmountLabel=""
        incentiveCapLabel={bonusCapLabel}
        learnMoreLink={learnMoreLink}
      />
    </>
  );
};

export default CSI;
